import React, { useState, useRef, FunctionComponent } from 'react';
import { ArrowDown } from '../../../icons/components';
import { useClickOutside } from '../../../utils';
import classNames from '../../../utils/classnames';
import { Highlighter } from '../Highlighter';
import { InputAppointmentForm, InputProps } from '../Input';

interface AutocompletSearchProps {
    input: InputProps;
    filterCallback: (searchValue: string) => AutocompleteItems;
    items?: AutocompleteItems;
    setValue?: (value: string) => void;
}

type AutocompleteItems = Array<{ searchterm: string }>;

export const AutcompleteSearch: FunctionComponent<AutocompletSearchProps> = ({
    input,
    filterCallback,
    items = [],
    setValue,
}) => {
    const [state, setState] = useState<{
        searchValue: string;
        isActive: boolean;
        items: AutocompleteItems;
        selectedValue: string;
        activeSuggestion: number | null;
        highlightedValue: string | '';
    }>({
        searchValue: '',
        isActive: false,
        items,
        selectedValue: '',
        activeSuggestion: null,
        highlightedValue: '',
    });

    const searchWrapper = useRef(null);

    useClickOutside(searchWrapper, () => {
        if (state.isActive) {
            setState((prevState) => ({
                ...prevState,
                isActive: false,
                highlightedValue: '',
                activeSuggestion: null,
                items: items,
            }));
        }
    });

    function setActiveSuggestion(type?: string) {
        const { activeSuggestion, items } = state;

        if (type === 'up' && activeSuggestion !== null) {
            if (activeSuggestion === 0) return;

            setState((prevState) => ({
                ...prevState,
                activeSuggestion: activeSuggestion - 1,
            }));
        } else {
            if (activeSuggestion === items.length - 1) {
                return;
            } else if (activeSuggestion === null) {
                setState((prevState) => ({
                    ...prevState,
                    activeSuggestion: 0,
                }));
            } else {
                setState((prevState) => ({
                    ...prevState,
                    activeSuggestion: (activeSuggestion || 0) + 1,
                }));
            }
        }
    }

    function closeAutocompleteList() {
        const { items, activeSuggestion, isActive } = state;

        if (isActive) {
            const searchValue = activeSuggestion !== null ? items[activeSuggestion].searchterm : '';
            setState((prevState) => ({
                ...prevState,
                isActive: false,
                searchValue,
                highlightedValue: searchValue,
                activeSuggestion: null,
            }));

            setValue && setValue(searchValue);
        }
    }

    function handleAutocompleteOnSuggestionClicked(suggestionValue: string) {
        setState((prevState) => ({
            ...prevState,
            isActive: false,
            items: items,
            searchValue: suggestionValue,
            highlightedValue: '',
            activeSuggestion: null,
        }));

        setValue && setValue(suggestionValue);
    }

    function handleInputOnChange(event: any) {
        const { value } = event.target;
        const hasValue = !!value.trim().length;

        if (!hasValue) {
            setState((prevState) => ({
                ...prevState,
                items: items,
                searchValue: value,
            }));
        } else {
            const filteredItems = filterCallback(value);
            setState((prevState) => ({
                ...prevState,
                items: [...filteredItems],
                isActive: true,
                highlightedValue: value,
                searchValue: value,
            }));
        }
    }

    function handleInputOnFocus() {
        setState((prevState) => ({
            ...prevState,
            isActive: true,
        }));
    }

    function handleInputOnKeyUp(event: any) {
        const key = window.event ? event.keyCode : event.which;

        switch (key) {
            // Arrow down
            case 40:
                setActiveSuggestion();
                break;

            // Arrow up
            case 38:
                setActiveSuggestion('up');
                break;

            // Escape
            case 27:
                setState((prevState) => ({
                    ...prevState,
                    isActive: false,
                    activeSuggestion: null,
                }));
                break;

            // Enter
            case 13:
                closeAutocompleteList();
                break;
        }
    }

    return (
        <div className="c-autocomplete-search" ref={searchWrapper}>
            <InputAppointmentForm
                {...input}
                validation={{
                    ...input.validation,
                    overwriteOnChange: handleInputOnChange,
                    onFocus: handleInputOnFocus,
                    onKeyUp: handleInputOnKeyUp,
                }}
            />

            <div className="autocomplete-search__icon">
                <ArrowDown width={13} height={8} />
            </div>
            <ul
                role="listbox"
                className={classNames('autocomplete-search__list', {
                    'is--active': state.isActive,
                })}>
                {!!state.items.length &&
                    state.items.map((item, idx) => (
                        <li
                            className="autocomplete-search__list-item"
                            role="option"
                            aria-selected={state.activeSuggestion === idx}
                            id={item.searchterm}
                            key={item.searchterm}
                            onClick={() => handleAutocompleteOnSuggestionClicked(item.searchterm)}>
                            <Highlighter
                                text={item.searchterm}
                                searchValue={state.highlightedValue}
                            />
                        </li>
                    ))}
            </ul>
        </div>
    );
};
